import { useState } from 'react'
import { Nav, Navbar } from 'react-bootstrap'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import {
  LightModeOutlined,
  DarkModeOutlined,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  PhotoCamera,
  NotificationsActive, NotificationsOff,
} from '@mui/icons-material'
import { useDispatch } from 'react-redux'

import { useLogout } from '../hooks/useLogout'
import useMyProfile from '../hooks/useMyProfile'
import { usePathContext } from '../context/path'
import { useAuthContext } from '../context/auth'
import { setMode } from '../state'
import FlexBetween from './FlexBetween'
import MyAvatar from './profile/MyAvatar'
import ProfileAvatarDialog from './profile/ProfileAvatarDialog'
import usePushNotifications from '../hooks/usePushNotifications'
import NavbarStatusStrip from './NavbarStatusStrip'
import { ROLES } from '../config/roles'

const Navbars = ({ 
  isSidebarOpen, 
  setIsSidebarOpen, 
  transparent = false,
  hideSidebarToggle = false,
}) => {
  const dispatch = useDispatch()
  const theme = useTheme()

  const { logout } = useLogout()
  const { auth } = useAuthContext()
  const { title } = usePathContext()
  const profile = useMyProfile()
  const push = usePushNotifications()

  const [menuAnchor, setMenuAnchor] = useState(null)
  const [photoOpen, setPhotoOpen] = useState(false)

  const roles = [].concat(auth?.roles ?? [])

  // Account menu: same palette as the dialogs (Edit user, My profile)
  const isDark = theme.palette.mode === 'dark'
  const accent = theme.palette.secondary.main

  // Role chips: same look as the Role column in the Users table
  const roleChipSx = (role) => ({
    height: 18,
    fontWeight: 600,
    fontSize: '0.68rem',
    '& .MuiChip-label': { px: 0.75 },
    // Root stands out by being filled, not by borrowing a status color
    color: role === ROLES.Root ? theme.palette.getContrastText(accent) : accent,
    borderColor: alpha(accent, 0.5),
    backgroundColor: role === ROLES.Root ? alpha(accent, 0.68) : alpha(accent, 0.08),   // Root: filled, a little transparent
  })

  const menuPaperSx = {
    mt: 0.75,
    backgroundColor: theme.palette.background.alt,
    color: theme.palette.text.primary,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : theme.palette.neutral[200]}`,
    borderRadius: 2,
    boxShadow: 8,
    '& .MuiMenu-list': { py: 0.5 },
    '& .MuiMenuItem-root': {
      gap: 0.5,
      py: 1,
      fontSize: '0.875rem',
      '&:hover': { backgroundColor: alpha(accent, 0.12) },
      '&.Mui-disabled': { opacity: 0.6 },
    },
    '& .MuiListItemIcon-root': { color: accent, minWidth: 32 },
  }

  // Sign out: quiet outline at rest, turns red on hover so it reads as "leave"
  const danger = theme.palette.error.main
  const logoutButtonSx = {
    // Hidden on mobile; Sign out stays available in the account menu
    display: { xs: 'none', lg: 'inline-flex' },
    ml: 0.5,
    height: 38,
    minWidth: { xs: 38, lg: 0 },
    px: { xs: 0, lg: 1.75 },
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : theme.palette.neutral[300]}`,
    backgroundColor: 'transparent',
    '& .MuiButton-startIcon': { mr: { xs: 0, lg: 1 }, ml: { xs: 0, lg: -0.25 } },
    '&:hover': {
      color: danger,
      borderColor: alpha(danger, 0.6),
      backgroundColor: alpha(danger, 0.08),
    },
  }

  return (
    <Navbar collapseOnSelect expand="lg" bg="none" variant="none">
      <div className="container-fluid" style={{ position: 'relative' }}>
        {/* Sidebar toggle — hidden for Guest users, and when logged out (no sidebar) */}
        {auth && !hideSidebarToggle && (
          <IconButton
            onClick={() => {
              try {
                window.dispatchEvent(
                  new CustomEvent('sidebarToggle', {
                    detail: { isOpen: !isSidebarOpen },
                  })
                )
              } catch (e) {}

              setIsSidebarOpen(!isSidebarOpen)
            }}
            title="Toggle Sidebar"
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logged out (Home / Login / Signup): nothing else on the left, so the title sits in the
            true centre of the bar instead of between the uneven left and right groups */}
        <Navbar.Brand
          style={
            auth
              ? { marginLeft: '12px' }
              : { position: 'absolute', left: '50%', transform: 'translateX(-50%)', margin: 0, pointerEvents: 'none' }
          }
        >
          <FlexBetween color={theme.palette.secondary.main}>
            <Box display="flex" alignItems="center" gap="0.5rem">
              <Typography variant="h3" fontWeight="bold">
                {title}
              </Typography>
            </Box>
          </FlexBetween>
        </Navbar.Brand>

        {/* Live status in the empty middle: outposts / users online, unread chats, clock */}
        <NavbarStatusStrip auth={auth} />

        <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        {/* Logged in: the status strip takes the middle, so this group stays its own size.
            Logged out: it fills the row so its links stay on the right. */}
        <Navbar.Collapse id="responsive-navbar-nav" style={{ flexGrow: auth ? 0 : 1 }}>
          <Nav className="ms-auto align-items-center">
            {/* Dark / light mode */}
            {/* The button shows (and is labelled with) the mode a click switches TO */}
            <IconButton
              onClick={() => dispatch(setMode())}
              title={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme.palette.mode === 'dark' ? (
                <LightModeOutlined sx={{ fontSize: '25px' }} />
              ) : (
                <DarkModeOutlined sx={{ fontSize: '25px' }} />
              )}
            </IconButton>

            {auth && (
              <>
                {/* Avatar + name + role (opens the account menu) */}
                <Box
                  role="button"
                  tabIndex={0}
                  title="Account"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setMenuAnchor(e.currentTarget)
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    mx: 2,
                    px: 1,
                    py: 0.5,
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: alpha(theme.palette.secondary.main, 0.12) },
                  }}
                >
                  <MyAvatar size={36} />

                  <Box sx={{ lineHeight: 1.2, textAlign: 'left' }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontWeight: 600, color: theme.palette.secondary[100], maxWidth: 180 }}
                    >
                      {auth.name}
                    </Typography>
                    {roles.length > 0 && (
                      <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.25}>
                        {roles.map((role) => (
                          <Chip key={role} size="small" variant="outlined" label={role} sx={roleChipSx(role)} />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>

                <Tooltip title="Sign out">
                  <Button
                    onClick={() => logout()}
                    startIcon={<LogoutIcon sx={{ fontSize: '1.15rem !important' }} />}
                    aria-label="Sign out"
                    sx={logoutButtonSx}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>
                      Sign out
                    </Box>
                  </Button>
                </Tooltip>

                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{ sx: menuPaperSx }}
                >
                  <Box display="flex" alignItems="center" gap={1.25} px={2} py={1.25} minWidth={240}>
                    <MyAvatar size={36} />
                    <Box minWidth={0}>
                      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                        {profile?.name || auth.name}
                      </Typography>
                      {profile?.email && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {profile.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Divider sx={{ borderColor: theme.palette.divider, my: '0 !important' }} />

                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null)
                      setPhotoOpen(true)
                    }}
                  >
                    <ListItemIcon>
                      <PhotoCamera fontSize="small" />
                    </ListItemIcon>
                    My profile
                  </MenuItem>

                  {push.supported && (
                    <MenuItem
                      disabled={push.busy || push.permission === 'denied'}
                      onClick={() => {
                        setMenuAnchor(null)
                        if (push.subscribed) push.disable()
                        else push.enable()
                      }}
                    >
                      <ListItemIcon>
                        {push.subscribed ? <NotificationsOff fontSize="small" /> : <NotificationsActive fontSize="small" />}
                      </ListItemIcon>
                      {push.permission === 'denied'
                        ? 'Notifications are blocked in the browser'
                        : push.subscribed
                        ? 'Turn off notifications when app is closed'
                        : 'Turn on notifications when app is closed'}
                    </MenuItem>
                  )}

                  <Divider sx={{ borderColor: theme.palette.divider, my: '4px !important' }} />

                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null)
                      logout()
                    }}
                    sx={{ color: danger, '&:hover': { backgroundColor: `${alpha(danger, 0.1)} !important` } }}
                  >
                    <ListItemIcon sx={{ color: `${danger} !important` }}>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Sign out
                  </MenuItem>
                </Menu>

                <ProfileAvatarDialog open={photoOpen} onClose={() => setPhotoOpen(false)} profile={profile} />
              </>
            )}

            {!auth && (
              <>
                <Nav.Link href="/login">
                  <FlexBetween color={theme.palette.secondary[100]}>Login</FlexBetween>
                </Nav.Link>
                <Nav.Link href="/signup">
                  <FlexBetween color={theme.palette.secondary[100]}>Signup</FlexBetween>
                </Nav.Link>
              </>
            )}
          </Nav>
        </Navbar.Collapse>
      </div>
    </Navbar>
  )
}

export default Navbars